import { useMemo, useState } from "react";
import { Fuel, Zap, PiggyBank } from "lucide-react";
import { useLanguage } from "@/i18n";
import { Reveal } from "./shared";

const Slider = ({ label, value, min, max, step, unit, onChange, testid }) => (
  <div>
    <div className="mb-1.5 flex items-baseline justify-between gap-3">
      <label htmlFor={testid} className="text-xs font-semibold text-neutral-600">{label}</label>
      <span className="font-mono text-sm font-bold text-neutral-900">
        {value} <span className="text-[10px] font-medium text-neutral-400">{unit}</span>
      </span>
    </div>
    <input
      id={testid}
      data-testid={testid}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full accent-brand-600"
    />
  </div>
);

export const TcoCalculator = () => {
  const { t, lang } = useLanguage();
  const c = t.tco;
  const [vehicles, setVehicles] = useState(25);
  const [dailyKm, setDailyKm] = useState(120);
  const [fuelPrice, setFuelPrice] = useState(6.5);
  const [fuelUse, setFuelUse] = useState(3.5);
  const [elecPrice, setElecPrice] = useState(1.0);
  const [evUse, setEvUse] = useState(3.5);

  const r = useMemo(() => {
    const comb = (fuelPrice * fuelUse) / 100;
    const ev = (elecPrice * evUse) / 100;
    const saveKm = Math.max(comb - ev, 0);
    return { comb, ev, monthVeh: saveKm * dailyKm * 30, yearFleet: saveKm * dailyKm * 365 * vehicles };
  }, [vehicles, dailyKm, fuelPrice, fuelUse, elecPrice, evUse]);

  const locale = lang === "pl" ? "pl-PL" : "en-US";
  const fmt = (n) => new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  const fmt0 = (n) => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(n));

  return (
    <Reveal delay={0.1}>
      <div className="mt-14 rounded-[1.75rem] border border-neutral-200 bg-white p-7 shadow-[0_30px_70px_-45px_rgba(44,56,28,0.4)] sm:p-10" data-testid="tco-calculator">
        <h3 data-testid="tco-heading" className="font-display text-2xl font-extrabold tracking-tight text-neutral-950 sm:text-3xl">
          {c.heading}
        </h3>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-500">{c.sub}</p>

        <div className="mt-9 grid gap-10 lg:grid-cols-2">
          <div className="grid gap-5 sm:grid-cols-2">
            <Slider label={c.vehicles} value={vehicles} min={1} max={500} step={1} unit="" onChange={setVehicles} testid="tco-slider-vehicles" />
            <Slider label={c.dailyKm} value={dailyKm} min={20} max={300} step={5} unit={c.km} onChange={setDailyKm} testid="tco-slider-dailykm" />
            <Slider label={c.fuelPrice} value={fuelPrice} min={4} max={12} step={0.1} unit={c.perL} onChange={setFuelPrice} testid="tco-slider-fuelprice" />
            <Slider label={c.fuelUse} value={fuelUse} min={2} max={8} step={0.1} unit={c.l100} onChange={setFuelUse} testid="tco-slider-fueluse" />
            <Slider label={c.elecPrice} value={elecPrice} min={0.5} max={3} step={0.05} unit={c.perKwh} onChange={setElecPrice} testid="tco-slider-elecprice" />
            <Slider label={c.evUse} value={evUse} min={2} max={8} step={0.1} unit={c.kwh100} onChange={setEvUse} testid="tco-slider-evuse" />
          </div>

          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-neutral-200 bg-paper p-5" data-testid="tco-comb-cost">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                  <Fuel size={13} /> {c.combLabel}
                </div>
                <div className="mt-2 font-display text-2xl font-black text-neutral-900 sm:text-3xl">{fmt(r.comb)} <span className="text-sm font-bold text-neutral-400">PLN/km</span></div>
              </div>
              <div className="rounded-2xl border border-brand-200 bg-brand-50 p-5" data-testid="tco-ev-cost">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-brand-700">
                  <Zap size={13} /> {c.evLabel}
                </div>
                <div className="mt-2 font-display text-2xl font-black text-brand-700 sm:text-3xl">{fmt(r.ev)} <span className="text-sm font-bold text-brand-500/70">PLN/km</span></div>
              </div>
            </div>
            <div className="flex flex-1 flex-col justify-between rounded-2xl bg-brand-900 p-6 text-white sm:p-7" data-testid="tco-savings-panel">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-400">
                <PiggyBank size={14} /> {c.savingsTitle}
              </div>
              <div className="mt-4 font-display text-4xl font-black tracking-tight sm:text-5xl" data-testid="tco-savings-year">
                {fmt0(r.yearFleet)} <span className="text-lg font-bold text-brand-400">PLN / {c.perYear}</span>
              </div>
              <div className="mt-3 font-mono text-xs text-white/70" data-testid="tco-savings-month">
                ≈ {fmt0(r.monthVeh)} PLN {c.perVehMonth}
              </div>
            </div>
          </div>
        </div>

        <p className="mt-6 border-t border-neutral-100 pt-4 font-mono text-[11px] leading-relaxed text-neutral-400" data-testid="tco-note">{c.note}</p>
      </div>
    </Reveal>
  );
};
